import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, PartialObserver } from 'rxjs';

import {
	OrganizationServices,
	OrganizationUsers
} from '../models/organization';
import { AUTH_SERVER_URL } from '../../config';
import { UserService } from '../../shared/services/user.service';
import { OrganizationHierarchy } from '../models/organization-hierarchy';
import { Visibility } from '../models/visibilities-info';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { FieldOfOrganizationUserInfo, OrganizationUserInfo } from '../models/organization-user-info';
import { OrganizationDetails, organizationDetailsFields } from '../models/organization-details';
import { OrderBy } from '../../shared/utils/constant';
import { AddServiceResponse } from '../models/user-service';
import { OrganizationBasics } from '../models/v2/organization-info';
import { OrganizationServiceResponse } from '../models/organization-service-info';
import { ParameterIdResponse } from '../models/v2/parameter-id-response';
import { ParameterAvailableValues } from '../models/v2/parameter-available-values';

const AUTH_ORGANIZATION = `${ AUTH_SERVER_URL }/v1/organizations:external`;
const GET_ORGANIZATION = `${ AUTH_SERVER_URL }/v2/organizations:external`;
const AUTH_V2 = `${ AUTH_SERVER_URL }/v2`;

@Injectable({ providedIn: 'root' })
export class OrganizationService {
	userId: number;
	private readonly orgNameSubject = new BehaviorSubject('');
	constructor(private readonly http: HttpClient, private readonly userService: UserService) {
		this.userId = this.userService.user.id;
	}

	setOrgName(orgName: string) {
		this.orgNameSubject.next(orgName);
	}

	subscribeToOrgName(observer: PartialObserver<string | undefined>) {
		return this.orgNameSubject.subscribe(observer);
	}

	findOrganizationDetails(
		organizationId: string
	) {
		const url = new URL(`${ GET_ORGANIZATION }/${ organizationId }`);
		const params = url.searchParams;
		params.append('fields', organizationDetailsFields.join(','));

		return this.http.get<OrganizationDetails>(url.toString());
	}

	findOrganizationHierarchy(organizationId: number) {
		const url = `${ GET_ORGANIZATION }/${ organizationId }/hierarchy`;
		return this.http.get<OrganizationHierarchy>(url);
	}

	getOrganizationVisibilities(organizationId: string) {
		const url = new URL(`${ GET_ORGANIZATION }/${ organizationId }/visibilities`);
		return this.http.get<PaginatedResult<Visibility>>(url.toString());
	}

	getOrganizationParentId(organizationId: string) {
		const url = new URL(`${ GET_ORGANIZATION }/${ organizationId }/parent`);
		return this.http.get<number>(url.toString());
	}

	getOrganizationUsers(
		organizationId: string,
		fields?: FieldOfOrganizationUserInfo[] | string[],
		filter?: string,
		page?: number,
		limit?: number,
		orderBy?: OrderBy<keyof OrganizationUserInfo>[] | OrderBy<keyof OrganizationUserInfo>,
		onlyCount = false,
		onlyDistinct = false
	) {
		const url = new URL(`${ GET_ORGANIZATION }/${ organizationId }/users`);
		const params = url.searchParams;
		if (fields) {
			params.append('fields', fields.join(','));
		}
		if (filter) {
			params.append('filter', filter);
		}
		if (page !== undefined) {
			params.append('page', String(page));
		}
		if (limit !== undefined) {
			params.append('limit', String(limit));
		}
		if (orderBy) {
			params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		}
		if (onlyCount) {
			params.append('onlyCount', String(onlyCount));
		}
		if (onlyDistinct) {
			params.append('distinct', String(onlyDistinct));
		}
		return this.http.get<PaginatedResult<OrganizationUserInfo>>(url.toString());
	}

	removeUser(id: number, userId: number) {
		const url = new URL(`${ GET_ORGANIZATION }/organizations/:${ id }/users/:${ userId }`);
		return this.http.delete(url.toString());
	}

	findOrganizationServices(organizationId: number) {
		const url = `${ AUTH_ORGANIZATION }/${ organizationId }/services`;
		return this.http.get<OrganizationServices>(url);
	}

	findOrganizationUsers(organizationId: string) {
		const url = `${ AUTH_ORGANIZATION }/${ organizationId }/users`;
		return this.http.get<OrganizationUsers>(url);
	}

	countOrganizationUsersToValidate(organizationId: string) {
		const url = `${ AUTH_ORGANIZATION }/${ organizationId }/count-users-to-validate`;
		return this.http.get<number>(url);
	}

	async organizationInfoToUpdate(organizationId: number, updatedOrgInfo: OrganizationBasics) {
		const url = `${ GET_ORGANIZATION }/${ organizationId }`;
		await firstValueFrom(this.http.put(url.toString(), updatedOrgInfo));
	}

	getOrganizationServices(
		organizationId: string,
		fields?: string[],
		filter?: string,
		index?: number,
		limit?: number,
		orderBy?: OrderBy<keyof OrganizationServiceResponse>[] | OrderBy<keyof OrganizationServiceResponse>,
		onlyDistinct = false
	) {
		const url = new URL(`${ GET_ORGANIZATION }/${ organizationId }/services`);
		const params = url.searchParams;
		if (fields) {
			params.append('fields', fields.join(','));
		}
		if (filter) {
			params.append('filter', filter);
		}
		if (index && limit) {
			params.append('page', String(Math.floor(index / limit) + 1));
			params.append('limit', String(limit));
		}
		if (orderBy) {
			params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		}
		if (onlyDistinct) {
			params.append('distinct', String(onlyDistinct));
		}

		return this.http.get<PaginatedResult<OrganizationServiceResponse>>(url.toString());
	}

	addServicesToOrganization(orgId: string, supplierId: number, services: AddServiceResponse[]) {
		const url = new URL(`${ GET_ORGANIZATION }/organizations/:${ orgId }/supplier/:${ supplierId }`);
		return this.http.post(url.toString(), services);
	}

	deleteServicesFromOrganization(
		orgId: string,
		supplierId: number,
		deleteServiceResponse: string[]
	) {
		const url = new URL(`${ GET_ORGANIZATION }/organizations/${ orgId }/supplier/${ supplierId }`);
		const params = url.searchParams;
		for (const service of deleteServiceResponse) {
			params.append('services', service);
		}
		return this.http.delete(url.toString());
	}

	async getParameterValuesList(parameters: number[]) {
		const url = new URL(`${ AUTH_V2 }/parameterValues`);
		const params = url.searchParams;
		for (const parameter of parameters) {
			params.append('parameterIds', parameter.toString());
		}
		return firstValueFrom(this.http.get<ParameterAvailableValues[]>(url.toString()));
	}

	async addServiceParametersToOrganization(
		orgId: number,
		servicesId: string,
		parametersRequest: ParameterIdResponse[]
	) {
		const url = new URL(`${ GET_ORGANIZATION }/organizations/${ orgId }/services/${ servicesId }/parameters`);
		return firstValueFrom(this.http.post(url.toString(), parametersRequest));
	}

	async updateServiceParametersOfOrganization(
		orgId: number,
		servicesId: string,
		parametersRequest: ParameterIdResponse[]
	) {
		const url = new URL(`${ GET_ORGANIZATION }/organizations/${ orgId }/services/${ servicesId }/parameters`);
		return firstValueFrom(this.http.put(url.toString(), parametersRequest));
	}
}
